import { Component, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription, interval } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { JobDefinition, JobLogLine, JobsService } from './jobs.service';
import { SharedService } from '../shared/services/shared.service';
import { InactivityService } from '../shared/services/inactivity.service';
import { NetworkSyncService } from '../shared/services/network-sync.service';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { SyncCheckModalComponent } from '../shared/modals/sync-check/sync-check-modal.component';
import { User } from '../shared/models/company';

interface ActiveRun {
  runId: string | null;
  jobKey: string;
  jobName: string;
  status: 'running' | 'success' | 'failed' | 'no-progress';
  lines: JobLogLine[];
  lastTs: string | null;
  startedAt: Date;
  finishedAt: Date | null;
  summary: any;
  error: string | null;
}

// Stop polling a single run after this long even if it never reports finished,
// so a stuck/forgotten tab doesn't poll forever.
const MAX_POLL_MS = 30 * 60 * 1000;
const POLL_INTERVAL_MS = 2000;

@Component({
  selector: 'app-developer-controls',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './developer-controls.component.html',
  styleUrl: './developer-controls.component.css',
})
export class DeveloperControlsComponent implements OnInit, OnDestroy {
  activeTab: 'jobs' | 'settings' = 'jobs';

  // Jobs
  jobs: JobDefinition[] = [];
  activeRuns: ActiveRun[] = [];
  triggeringKeys = new Set<string>();
  loadError: string | null = null;

  // Settings/Developer Options
  useAzureServer: boolean = true;
  inactivityTimerEnabled: boolean = true;
  inactivityTimeoutMinutes: number = 15; // default 15 minutes
  forceOffline: boolean = false;

  private pollSubs = new Map<string, Subscription>();

  constructor(
    private jobsService: JobsService,
    public sharedService: SharedService,
    public inactivityService: InactivityService,
    public networkSync: NetworkSyncService,
    private modalService: BsModalService,
    public bsModalRef: BsModalRef,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.jobsService.getDefinitions().subscribe({
      next: (res) => {
        this.jobs = res.jobs;
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.loadError = err?.error?.message || 'Failed to load job list';
        this.cdr.markForCheck();
      },
    });

    // Load developer options from localStorage. Unset defaults to Azure —
    // only an explicit 'false' switches to the local server.
    this.useAzureServer = localStorage.getItem('use_azure_server') !== 'false';

    const lsInactivityVal = localStorage.getItem('inactivity_timer_enabled');
    this.inactivityTimerEnabled = lsInactivityVal !== 'false';

    const lsTimeoutMs = localStorage.getItem('inactivity_timeout_ms');
    if (lsTimeoutMs) {
      const parsed = parseInt(lsTimeoutMs, 10);
      if (!isNaN(parsed) && parsed > 0) {
        this.inactivityTimeoutMinutes = Math.round(parsed / 60000);
      }
    }

    const lsForceOfflineVal = localStorage.getItem('force_offline');
    this.forceOffline = lsForceOfflineVal === 'true';
  }

  ngOnDestroy(): void {
    this.pollSubs.forEach((sub) => sub.unsubscribe());
    this.pollSubs.clear();
  }

  trigger(job: JobDefinition): void {
    if (this.triggeringKeys.has(job.key)) return;
    this.triggeringKeys.add(job.key);

    this.jobsService.trigger(job.key).subscribe({
      next: (res) => {
        this.triggeringKeys.delete(job.key);

        const run: ActiveRun = {
          runId: res.run_id,
          jobKey: job.key,
          jobName: job.name,
          status: res.run_id ? 'running' : 'no-progress',
          lines: [],
          lastTs: null,
          startedAt: new Date(res.triggeredAt || Date.now()),
          finishedAt: null,
          summary: res.run_id ? null : res.response,
          error: null,
        };
        this.activeRuns.unshift(run);
        this.cdr.markForCheck();

        if (run.runId) {
          this.startPolling(run);
        }
      },
      error: (err) => {
        this.triggeringKeys.delete(job.key);
        this.activeRuns.unshift({
          runId: null,
          jobKey: job.key,
          jobName: job.name,
          status: 'failed',
          lines: [],
          lastTs: null,
          startedAt: new Date(),
          finishedAt: new Date(),
          summary: null,
          error: err?.error?.message || err.message || 'Failed to trigger job',
        });
        this.cdr.markForCheck();
      },
    });
  }

  private startPolling(run: ActiveRun): void {
    const runId = run.runId as string;
    const deadline = Date.now() + MAX_POLL_MS;

    const sub = interval(POLL_INTERVAL_MS)
      .pipe(
        switchMap(() =>
          this.jobsService.getLogs(runId, run.lastTs).pipe(
            switchMap((logsRes) => {
              if (logsRes.lines.length) {
                run.lines.push(...logsRes.lines);
                run.lastTs = logsRes.lines[logsRes.lines.length - 1].ts;
                this.cdr.markForCheck();
              }
              return this.jobsService.getStatus(runId);
            })
          )
        )
      )
      .subscribe({
        next: (statusRes) => {
          if (statusRes.finished) {
            run.status = statusRes.status === 'success' ? 'success' : 'failed';
            run.summary = statusRes.summary;
            run.finishedAt = new Date();
            this.stopPolling(runId);
            this.cdr.markForCheck();
          } else if (Date.now() > deadline) {
            this.stopPolling(runId);
          }
        },
        error: () => {
          // Transient polling error — leave the run marked running and try again
          // next tick rather than flipping it to failed on a single blip.
        },
      });

    this.pollSubs.set(runId, sub);
  }

  private stopPolling(runId: string): void {
    this.pollSubs.get(runId)?.unsubscribe();
    this.pollSubs.delete(runId);
  }

  dismiss(run: ActiveRun): void {
    if (run.runId) this.stopPolling(run.runId);
    this.activeRuns = this.activeRuns.filter((r) => r !== run);
    this.cdr.markForCheck();
  }

  elapsedSeconds(run: ActiveRun): number {
    const end = run.finishedAt || new Date();
    return Math.max(0, Math.round((end.getTime() - run.startedAt.getTime()) / 1000));
  }

  trackByRun(_index: number, run: ActiveRun): string {
    return run.runId || `${run.jobKey}-${run.startedAt.getTime()}`;
  }

  toggleAzureServer(): void {
    this.useAzureServer = !this.useAzureServer;
    localStorage.setItem('use_azure_server', String(this.useAzureServer));

    // The session cookie is scoped to whichever origin issued it, so a session
    // valid on the old target is meaningless on the new one. Force a clean
    // re-login against the newly selected backend instead of leaving the app
    // in a half-authenticated state that floods the new server with 401s.
    this.sharedService.logged_in_user = new User();
    localStorage.removeItem('cached_user');
    this.bsModalRef.hide();
    window.location.href = '/login';
  }

  toggleInactivityTimer(): void {
    this.inactivityTimerEnabled = !this.inactivityTimerEnabled;
    localStorage.setItem('inactivity_timer_enabled', String(this.inactivityTimerEnabled));
    if (this.inactivityTimerEnabled) {
      this.inactivityService.startWatching();
    } else {
      this.inactivityService.stopWatching();
    }
    this.cdr.markForCheck();
  }

  updateInactivityTimeout(): void {
    const timeoutMs = this.inactivityTimeoutMinutes * 60 * 1000;
    localStorage.setItem('inactivity_timeout_ms', String(timeoutMs));
    this.cdr.markForCheck();
  }

  toggleForceOffline(): void {
    this.forceOffline = !this.forceOffline;
    localStorage.setItem('force_offline', String(this.forceOffline));
    this.networkSync.isOnline$.next(!this.forceOffline);
    this.cdr.markForCheck();
  }

  openSyncCheck(): void {
    this.modalService.show(SyncCheckModalComponent, {
      backdrop: 'static',
      keyboard: false,
    });
  }

  close(): void {
    this.bsModalRef.hide();
  }
}
